import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { frontloadConnect } from 'react-frontload';
import get from 'lodash/get';
import { withCookies, Cookies } from 'react-cookie';
import MainContent from './MainContent';
import DefaultLayout from '../../../layouts/Default';
import * as actions from '../../../store/actions';
import ErrorMessage from '../../../components/ErrorMessage';
import * as utils from '../../../store/utils';
import {
  generateSearchPath,
  getDefaultParams,
  reduceURLForRedirect
} from '../../../utils/urlHelpers/engines';
import { getParamsFromLocationAndHistoryQueryString } from '../../../utils/featureToggles';
import { REDUCTION_REDIRECT_COOKIE } from '../../../constants/cookies';
import { getBoatsConstants } from '../../../constants/boats';
import { PortalConfigContext } from '../../../config/portal';
import { runOnce } from '../../../utils/runOnceHelper';
import { setCookie } from '../../../utils/cookies';
import {getParserEngineCommonFunctions} from '../../../utils/urlHelpers/engineConstantsParsers';
import {getRouteConstantsFromI18n} from '../../../tppServices/translations/constants';

const Engines = (props) => {
  const context = useContext(PortalConfigContext);
  const routesConstants = getRouteConstantsFromI18n();
  const hasNoListings = () => props.search.count < 1;
  const { parseEnginePageParams } = getParserEngineCommonFunctions(null, context);
  const isNotRoot = () =>
    `${props.location.pathname}/` !== routesConstants.SEARCH_URL_ROOT;
  const isPageVisible = () => {
    const pageSize = parseInt(get(props, 'params.pageSize', '28'));
    const maxVisiblePage = Math.ceil(props.search.count / pageSize);
    const pageParam = get(props, 'match.params.page', '');
    const { page } = parseEnginePageParams(pageParam);

    return page <= maxVisiblePage;
  };

  if (
    utils.isServer() &&
    isNotRoot() &&
    props.success &&
    (hasNoListings() || !isPageVisible())
  ) {
    const { cookies } = props;
    setCookie(cookies, REDUCTION_REDIRECT_COOKIE, true);
  }

  const {
    location: { pathname }
  } = props;
  const isLowerCase = pathname && pathname === pathname.toLowerCase();
  const searchType = getBoatsConstants().SEARCH_TYPES.engines;

  if (utils.isServer() && isNotRoot() && hasNoListings() && props.success) {
    let redirectUrl = reduceURLForRedirect(props, searchType);
    return <Redirect to={{ pathname: redirectUrl }} {...props} />;
  }

  if (utils.isServer() && props.success && !isLowerCase) {
    return <Redirect to={{ pathname: pathname.toLowerCase() }} {...props} />;
  }

  const pageParam = get(props, 'match.params.page', '');
  const maxPages = get(context, 'pages.searchResults.pagination.maxPages', 357);
  const { page } = parseEnginePageParams(pageParam);
  if (page > maxPages) {
    return (
      <DefaultLayout {...props} pageType="SearchResults">
        <ErrorMessage {...props} message="404" />
      </DefaultLayout>
    );
  }

  if (utils.isServer() && isNotRoot() && props.success && !isPageVisible()) {
    const params = get(props, 'match.params', {});
    const redirectUrl = generateSearchPath(
      {},
      getDefaultParams(params),
      true,
      undefined,
      searchType
    );
    return <Redirect to={{ pathname: redirectUrl }} {...props} />;
  }

  return (
    <DefaultLayout {...props} pageType="SearchResults">
      {props.errors ? (
        <ErrorMessage {...props} />
      ) : (
        <MainContent searchType={searchType} {...props} />
      )}
    </DefaultLayout>
  );
};

const mapStateToProps = (state) => {
  return {
    params: get(state.app, 'params', {}),
    search: get(state.app, 'data.search', {}),
    sponsored: get(state.app, 'data.sponsored', {}),
    facets: get(state.app, 'data.facets', {}),
    isWorking: state.app.isWorking,
    componentWorking: state.app.componentWorking,
    success: state.app.success,
    errors: state.app.errors,
    message: state.app.message,
    statusCode: state.app.statusCode
  };
};

Engines.propTypes = {
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  errors: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  }).isRequired,
  search: PropTypes.shape({
    count: PropTypes.number
  }).isRequired,
  success: PropTypes.bool
};

// we create a function that will run only once bypassing multiple renders.
const justFirstTime = runOnce();

export default withCookies(
  connect(
    mapStateToProps,
    null
  )(
    frontloadConnect(
      ({
        dispatch,
        history,
        location,
        cookies,
        debug = process.env.REACT_APP_LOCAL_DEBUG
      }) => {
        const otherParams = getParamsFromLocationAndHistoryQueryString({
          location,
          history
        });

        if (utils.isServer()) {
          return dispatch(
            actions.getEnginesData(
              location.pathname,
              cookies.cookies,
              otherParams
            )
          );
        }

        if (actions.shouldGetData(location)) {
          return dispatch(
            actions.getEnginesData(
              location.pathname,
              cookies.cookies,
              otherParams
            )
          );
        }

        if (justFirstTime() && debug) {
          return dispatch(
            actions.getEnginesData(
              location.pathname,
              cookies.cookies,
              otherParams
            )
          );
        }
      },
      {
        onUpdate: true,
        onMount: true
      }
    )(Engines)
  )
);
